import { oktaAuth } from '@config/oktaConfig';
import { store } from '@store/store';

type AsappSDK = {
  (...args: unknown[]): void;
  _?: unknown[][];
  Host?: string;
  isInitialized?: boolean;
};

declare global {
  interface Window {
    ASAPP?: AsappSDK;
  }
}

// SDK finished initializing (set in onLoadComplete).
let isAsappReady = false;
// A chat-open was requested before the SDK was ready; replayed in onLoadComplete.
let isShowPending = false;
// SDK injection/load already kicked off; guards against re-loading.
let isAsappLoadStarted = false;

const ASAPP_SDK_HOST = 'https://sdk.asapp.com';
const ASAPP_LOGIN_URL = '/login';

type AsappContext = {
  Segments: string[];
  CompanySubdivision: string;
  CustomerId?: string;
  CustomerInfo: {
    customerType: string;
  };
  Auth?: {
    Token: string;
  };
};

const isAsappInitialized = (): boolean => isAsappReady || !!window.ASAPP?.isInitialized;

/** Current customer number from the Redux account state, if available. */
const getCustomerId = (): string | undefined =>
  store.getState().account.getUser?.customer.customerNumber;

/** Current Okta access token, if the user is authenticated. */
const getAuthToken = (): string | undefined => {
  try {
    return oktaAuth.getAccessToken() || undefined;
  } catch {
    return undefined;
  }
};

/**
 * Builds the ASAPP customer context including the Okta auth token when the
 * user is authenticated, so chat actions requiring authentication succeed
 * without a second login.
 */
const buildAsappContext = (): AsappContext => {
  const token = getAuthToken();
  const customerId = getCustomerId();
  const context: AsappContext = {
    Segments: ['auspl'],
    CompanySubdivision: 'auspl',
    CustomerInfo: {
      customerType: 'auspl',
    },
  };
  // Only include customer identity fields after login
  if (token && customerId) {
    context.CustomerId = customerId;
    context.Auth = { Token: token };
  }
  return context;
};

/** Injects and initializes the ASAPP SDK exactly once. */
export const loadASAPP = (): void => {
  if (isAsappLoadStarted || window.ASAPP?.isInitialized) return;
  isAsappLoadStarted = true;

  const urlParams = new URLSearchParams(window.location.search);
  const shouldAutoOpen = urlParams.get('chat') === 'open';

  // Set up the queue stub so calls made before the SDK loads are replayed.
  if (!window.ASAPP) {
    const queue: unknown[][] = [];
    const fn = (...args: unknown[]): void => {
      queue.push(args);
    };
    window.ASAPP = Object.assign(fn, {
      _: queue,
      Host: ASAPP_SDK_HOST,
    });
  }

  const script = document.createElement('script');
  script.async = true;
  script.src = `${ASAPP_SDK_HOST}/chat-sdk.js`;
  script.type = 'text/javascript';
  document.body.appendChild(script);

  window.ASAPP('load', {
    APIHostname: import.meta.env.VITE_ASAPP_API_HOSTNAME || 'platform.asapp.com',
    AppId: import.meta.env.VITE_ASAPP_APP_ID || 'assurantautomotive-sandbox',
    Display: {
      Align: 'right',
      VerticalOffset: 35,
      Identity: 'auspl',
    },
    ContextProvider(callback: (ctx: AsappContext) => void) {
      callback(buildAsappContext());
    },
    // Redirect to login when the session expires mid-chat.
    UserLoginHandler() {
      window.location.assign(ASAPP_LOGIN_URL);
    },
    onLoadComplete() {
      isAsappReady = true;
      // Refresh customer identity with the latest token once the SDK is ready.
      window.ASAPP?.('setCustomer', {
        ContextProvider(callback: (ctx: AsappContext) => void) {
          callback(buildAsappContext());
        },
      });

      if (shouldAutoOpen || isShowPending) {
        isShowPending = false;
        window.ASAPP?.('show');
      }
    },
  });
};

/**
 * Pushes the current authenticated identity to ASAPP after a successful login.
 * Call this once the customer details are available in the Redux store.
 */
export const updateASAPPCustomer = (): void => {
  if (!isAsappInitialized() || !window.ASAPP) return;
  window.ASAPP('setCustomer', {
    ContextProvider(callback: (ctx: AsappContext) => void) {
      callback(buildAsappContext());
    },
  });
};

/** Opens the ASAPP chat widget, deferring the show until the SDK is ready. */
export const openASAPPChat = (): void => {
  if (typeof window === 'undefined') return;

  loadASAPP();

  if (isAsappInitialized() && window.ASAPP) {
    window.ASAPP('show');
  } else {
    isShowPending = true;
  }
};
